import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { contentUpload } from "../../services/content";

type Props = {
  title?: string;
  accept?: string;
  multiple?: boolean;
  destPrefix: string; // e.g. "uploads/products"
  branch?: string;
  onUploaded: (paths: string[]) => void;
};

const sanitizeName = (name: string) => name.replace(/[^a-zA-Z0-9_.-]+/g, "-");

const MediaUploader: React.FC<Props> = ({
  title,
  accept,
  multiple,
  destPrefix,
  branch,
  onUploaded,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [errors, setErrors] = React.useState<string>("");

  const handleChoose = () => inputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setErrors("");
    setUploading(true);
    const uploaded: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const fname = `${Date.now()}-${sanitizeName(f.name)}`;
        const dest = `${destPrefix.replace(/\/$/, "")}/${fname}`;
        const res = await contentUpload(f, dest, branch);
        if (res?.ok && res?.path) {
          // Convert repo path to public path
          const publicPath = `/${res.path.replace(
            /^.*?frontend\/public\/?/,
            ""
          )}`.replace(/\\/g, "/");
          uploaded.push(publicPath);
        }
      }
      if (uploaded.length > 0) onUploaded(uploaded);
    } catch (err: any) {
      setErrors(err?.message || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title || "Dosya Yükle"}
        </Typography>
        {errors && <Chip color="error" label={errors} />}
      </Stack>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={onFileChange}
      />
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={handleChoose}
        disabled={uploading}
      >
        {uploading ? "Yükleniyor..." : "Dosya Seç"}
      </Button>
    </Box>
  );
};

export default MediaUploader;
